import { motion } from 'framer-motion';
import { Shield, Mail, Phone, Linkedin, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';

// Custom X (formerly Twitter) icon
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const Footer = () => {
  const logoUrl = 'https://customer-assets-eiarnc6j.emergentagent.net/job_ot-partners/artifacts/sglfjlo8_OT_Resilience_LOGO-removebg-preview.png';
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { Icon: Linkedin, href: 'https://linkedin.com/company/ot-resilience', label: 'LinkedIn' },
    { Icon: XIcon, href: 'https://x.com/theotresilience?s=21', label: 'X' },
    { Icon: Instagram, href: 'https://www.instagram.com/OTresilience/', label: 'Instagram' }
  ];

  const footerLinks = {
    Services: [
      { label: 'Design' },
      { label: 'Integration' },
      { label: 'Operations' },
      { label: 'Engineering' },
      { label: 'Risk Management' },
      { label: 'Maintenance' }
    ],
    Company: [
      { label: 'About Us', href: '/about', route: true },
      { label: 'Careers', href: 'https://linkedin.com/company/ot-resilience', external: true },
      { label: 'Contact', href: '/contact', route: true }
    ]
  };

  return (
    <footer className="relative bg-slate-950 text-white pt-20 pb-8 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Top Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={logoUrl} 
                alt="OT Resilience" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-slate-400 leading-relaxed mb-6">
                Securing critical infrastructure with specialized OT cybersecurity expertise. Trusted by industry leaders worldwide.
              </p>
              {/* Social Links */}
              <div className="flex gap-4">
                {socialLinks.map(({ Icon, href, label }, i) => (
                  <motion.a
                    key={i}
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    aria-label={label}
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg flex items-center justify-center hover:bg-amber-400 hover:border-amber-400 transition-all duration-300 group"
                  >
                    <Icon className="w-5 h-5 text-white group-hover:text-slate-950 transition-colors duration-300" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([title, links], i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className={i === 0 ? 'lg:col-start-4' : ''}
            >
              <h4 className="text-lg font-bold text-white mb-4">{title}</h4>
              <ul className="space-y-3">
                {links.map((link, j) => {
                  const linkLabel = typeof link === 'string' ? link : link.label;
                  const linkHref = typeof link === 'string' ? '#' : link.href;
                  const isExternal = typeof link !== 'string' && link.external;
                  const isRoute = typeof link !== 'string' && link.route;
                  const linkClass = "text-slate-400 hover:text-amber-400 transition-colors duration-300 inline-block hover:translate-x-1 transform";
                  return (
                    <li key={j}>
                      {title === 'Services' ? (
                        <span className="text-slate-400 inline-block">
                          {linkLabel}
                        </span>
                      ) : isRoute ? (
                        <Link
                          to={linkHref}
                          onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                          className={linkClass}
                        >
                          {linkLabel}
                        </Link>
                      ) : (
                        <a
                          href={linkHref}
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? 'noopener noreferrer' : undefined}
                          className={linkClass}
                        >
                          {linkLabel}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            © {currentYear} OT Resilience. All rights reserved.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex gap-6"
          >
            <Link
              to="/privacy"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="hover:text-amber-400 transition-colors duration-300"
            >
              Privacy Policy
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Decorative Element */}
      <motion.div
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.05, 0.1, 0.05]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-amber-500 rounded-full blur-[150px]"
      />
    </footer>
  );
};
